import Component from '@glimmer/component';
import Table from 'tio-reimagined/components/table';
import Paginator from 'tio-reimagined/components/paginator';
import { LinkTo } from '@ember/routing';
import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { eq } from 'tio-ui/utilities';
import { tracked } from '@glimmer/tracking';
import type { ProgramsSearchResult } from 'tio-common/models/search';
import { snakeToCamelCase } from 'tio-common/utils/string-case-conversion';
import { reorderTableColumns } from '../../utils/table-utils';
import {
  autoGenerateLabel,
  generateColumnLabel,
  generateSmartLabel,
} from '../../utils/label-generation';
import { TAS_PROGRAMS_COLUMN_MAPPING } from '../../utils/tas-column-config';

// ============================================================================
// DATA FLOW RULES - READ THIS FIRST!
// ============================================================================
//
// **FIELD NAME FORMATS & NORMALIZATION**
//
// There are TWO formats for field names in this system:
//
// 1. **OPENSEARCH / QUERY PARAMS** (snake_case):
//    - How OpenSearch stores fields internally
//    - What we send in API requests (_source parameter)
//    - What column selector UI displays
//    - Example: employee_name_keyword, tas_application_field_courses_begin_date_date
//
// 2. **API RESPONSE DATA** (camelCase):
//    - What Rails returns after deep_camelize_keys()
//    - What item properties are named
//    - Example: employeeNameKeyword, tasApplicationFieldCoursesBeginDateDate
//
// **NORMALIZATION STRATEGY**:
// To avoid complexity of handling both formats throughout the code, we normalize
// to camelCase as early as possible:
//
// 1. **Entry Point** (displayColumns getter):
//    - selectedColumns arrive as snake_case from query params
//    - Immediately convert to camelCase using snakeToCamelCase()
//    - Store camelCase in column.key for internal use
//
// 2. **Internal Processing** (camelCase only):
//    - generateSmartLabel() receives camelCase only
//    - getCellValue() receives camelCase only (matches API response)
//    - All column keys in displayColumns are camelCase
//
// 3. **Benefits**:
//    - Simpler code - no dual format handling needed
//    - Matches API response format directly
//    - Consistent with frontend camelCase convention
//
// **LABEL GENERATION**:
// - generateSmartLabel() receives camelCase column keys
// - Strips suffixes (Keyword, Date, Text, Boolean, etc.)
// - Strips TAS prefixes (tasProgramInstanceField, tasApplication, etc.)
// - For ambiguous fields (id, state, name), keeps model context
// - Generates clean Title Case labels
//
// ============================================================================
// OTHER IMPORTANT RULES
// ============================================================================
//
// 1. **API RETURNS CAMELCASE**: All data from backend comes in camelCase
//    Example: { employeeName: "John", tasProgramInstanceFieldName: "MBA" }
//
// 2. **QUERIES USE SNAKE_CASE**: Filters and column selectors send snake_case
//    Example: employee_name_keyword, tas_program_template_field_program_name_keyword
//
// 3. **UI REFLECTS CURRENT QUERY**: Column selector shows what's being queried
//    - If selectedColumns includes "employee_name_keyword", that box is checked
//    - Default columns should use snake_case keys to match column selector
//
// 4. **COLUMN KEYS FROM MAPPING**: Column keys come from COLUMN_MAPPING object
//    - Keys are snake_case (what UI uses to identify columns)
//    - Values include label, type, and linkable route
//
// 5. **LABELS**: Auto-generated from field names, with COLUMN_MAPPING overrides
//    - generateSmartLabel() handles both camelCase and snake_case input
//    - Strips metadata suffixes (Keyword, Date, Text, etc.)
//    - Strips TAS model prefixes for cleaner labels
//    - COLUMN_MAPPING only needed when label should differ from auto-generated
//    - Example: "tas_program_instance_field_school_institution_name_keyword" → "School"
//
// ============================================================================

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ColumnKey = keyof typeof COLUMN_MAPPING;
type ColumnType = 'text' | 'currency' | 'date' | 'boolean' | 'array-primitive' | 'array-objects';

/**
 * Configuration for displaying nested object fields.
 * Used to control which fields are shown from array objects (e.g., courses).
 */
interface NestedFieldConfig {
  /** Field key in the nested object (can be camelCase or snake_case) */
  readonly field: string;
  /** Optional custom label for the field. Use false to show value only without label. If undefined, auto-generates label. */
  readonly label?: string | false;
  /** Optional type override for the field */
  readonly type?: ColumnType;
}

/**
 * Configuration for array-objects column type.
 * Allows specifying which nested fields to display and how to format them.
 */
interface ArrayObjectsConfig {
  /** Fields to display from each nested object */
  readonly fields: readonly NestedFieldConfig[];
  /** Separator between field entries (default: '\n') */
  readonly separator?: string;
}

interface DisplayColumn {
  key: string;
  label: string;
  type: ColumnType;
  linkable: string | false;
  /** Configuration for array-objects type - which nested fields to show */
  arrayConfig?: ArrayObjectsConfig;
}

export interface ProgramsTableSignature {
  Args: {
    results: ProgramsSearchResult;
    isLoading: boolean;

    // Column selection
    selectedColumns?: string[];
    columnOrder?: string[];

    // Base route for constructing links (e.g., "authenticated.tuition-assistance" or "authenticated.admin.tas")
    baseRoute?: string;

    // Display options
    showIndividualApplications?: boolean;
    excludeNonVoucherPrograms?: boolean;
    courseCashProgramsOnly?: boolean;
    includeCourses?: boolean;
    includeInvoiceDate?: boolean;
    terminatedEmployees?: boolean;
    excludeTermVerified?: boolean;

    // Legacy routes for backward compatibility
    applicationDetailRoute?: string;
    programDetailRoute?: string;

    // Payment states to show
    paymentStates?: string;

    // Pagination
    limit: number;
    page: number;

    // Filter data for empty state
    selectedFilters?: Record<string, string | null>;

    // Callbacks
    onPageChange: (page: number) => void;
    onColumnOrderChange?: (order: string[]) => void; // ✅ NEW: Callback for column reorder events
  };
  Element: HTMLDivElement;
}

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const DATE_FIELD_PATTERNS = [
  /_at$/, // ends with _at (created_at, updated_at, etc.)
  /_date$/, // ends with _date
  /^date_/, // starts with date_
  /_time$/, // ends with _time
  /timestamp/i, // contains timestamp
  /created/i, // contains created
  /updated/i, // contains updated
  /modified/i, // contains modified
  /deleted/i, // contains deleted
  /expired/i, // contains expired
] as const;

const CURRENCY_FIELD_PATTERNS = [
  /amount$/i, // ends with amount
  /amount_integer$/i, // ends with amount_integer (OpenSearch integer cents)
  /amountInteger$/i, // ends with AmountInteger (camelCase integer cents)
  /price$/i, // ends with price
  /cost$/i, // ends with cost
  /fee$/i, // ends with fee
  /tuition$/i, // ends with tuition
  /salary$/i, // ends with salary
  /wage$/i, // ends with wage
  /total$/i, // ends with total
  /balance$/i, // ends with balance
] as const;

// ============================================================================
// COLUMN MAPPING - Now imported from shared configuration
// ============================================================================
// Using TAS_PROGRAMS_COLUMN_MAPPING from tas-column-config.ts
// This ensures consistent column configuration across individual and aggregate tables
const COLUMN_MAPPING = TAS_PROGRAMS_COLUMN_MAPPING;

// ============================================================================
// STRING UTILITIES
// ============================================================================

/**
 * Auto-generates human-readable labels from field names.
 * Handles both camelCase and snake_case input, converts to Title Case.
 *
 * @future-extraction tio-common/utils/string-helpers
 */
// ============================================================================
// TYPE INFERENCE UTILITIES
// ============================================================================

/**
 * Infers column type based on field name patterns and sample value.
 * Used for dynamic column type detection when column mapping is not available.
 *
 * @future-extraction tio-common/utils/column-type-inference
 */
const inferColumnType = (fieldName: string, sampleValue?: unknown): ColumnType => {
  // Check field name patterns for dates
  const isDateField = DATE_FIELD_PATTERNS.some((pattern) => pattern.test(fieldName));

  // Check for currency patterns
  const isCurrencyField = CURRENCY_FIELD_PATTERNS.some((pattern) => pattern.test(fieldName));

  // If we have a sample value, check its format for runtime type detection
  if (sampleValue !== undefined && sampleValue !== null && sampleValue !== '') {
    const valueStr = String(sampleValue);

    // Check if the value is an array
    if (Array.isArray(sampleValue)) {
      // If empty array, default to text
      if (sampleValue.length === 0) {
        return 'text';
      }

      // Check the first element to determine array type
      const firstElement = sampleValue[0];

      // If first element is an object (but not null/date), it's array of objects
      if (
        typeof firstElement === 'object' &&
        firstElement !== null &&
        !(firstElement instanceof Date)
      ) {
        return 'array-objects';
      }

      return 'array-primitive';
    }

    // Check if value looks like a serialized array (starts with [ and contains objects)
    if (
      valueStr.startsWith('[') &&
      (valueStr.includes('[object Object]') || valueStr.includes('object Object'))
    ) {
      return 'array-objects';
    }

    // Check if value looks like a simple serialized array
    if (valueStr.startsWith('[') && valueStr.endsWith(']') && !valueStr.includes('object Object')) {
      return 'array-primitive';
    }

    // Check if value looks like an ISO date string
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    const shortDatePattern = /^\d{4}-\d{2}-\d{2}$/;
    const timestampPattern = /^\d{10,13}$/; // Unix timestamp (10-13 digits)

    if (
      isoDatePattern.test(valueStr) ||
      shortDatePattern.test(valueStr) ||
      timestampPattern.test(valueStr)
    ) {
      return 'date';
    }

    // Check if value looks like currency (number or string with decimal)
    if (isCurrencyField && (typeof sampleValue === 'number' || /^\d+\.?\d*$/.test(valueStr))) {
      return 'currency';
    }
  }

  // Field name based inference
  if (isDateField) return 'date';
  if (isCurrencyField) return 'currency';

  return 'text';
};

// ============================================================================
// ARRAY FORMATTING UTILITIES
// ============================================================================

/**
 * Formats arrays of primitive values (strings, numbers, booleans) into
 * comma-separated strings.
 *
 * @future-extraction tio-common/utils/array-formatters
 */
const formatArrayPrimitive = (array: unknown) => {
  // Handle actual arrays
  if (Array.isArray(array)) {
    if (array.length === 0) return '';

    return array
      .filter((item) => item !== null && item !== undefined && item !== '')
      .map((item) => String(item))
      .join(', ');
  }

  // Handle serialized arrays or string representations
  const arrayStr = String(array);
  if (arrayStr.startsWith('[') && arrayStr.endsWith(']')) {
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(arrayStr);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((item) => item !== null && item !== undefined && item !== '')
          .map((item) => String(item))
          .join(', ');
      }
    } catch {
      // If JSON parsing fails, treat as string and clean up
      return arrayStr
        .replace(/^\[|\]$/g, '') // Remove brackets
        .split(',')
        .map((item) => item.trim().replace(/^"|"$/g, '')) // Remove quotes
        .filter((item) => item && item !== '')
        .join(', ');
    }
  }

  return String(array);
};

/**
 * Formats arrays of objects with key-value display and separators.
 * Displays objects with labeled fields and separates multiple objects with dividers.
 *
 * @param array - The array of objects to format
 * @param config - Optional configuration for which fields to show
 * @future-extraction tio-common/utils/array-formatters
 */
const formatArrayObjects = (array: unknown, config?: ArrayObjectsConfig) => {
  // Handle actual arrays
  if (Array.isArray(array)) {
    if (array.length === 0) return '';

    return array
      .filter((item) => item !== null && item !== undefined)
      .map((item, index) => {
        if (typeof item !== 'object' || item === null) {
          return String(item);
        }

        const obj = item as Record<string, unknown>;

        // If config is provided, try to show only configured fields
        if (config?.fields && config.fields.length > 0) {
          const entries = config.fields
            .map((fieldConfig) => {
              // Try both camelCase and snake_case versions of the field name
              const camelCaseField = fieldConfig.field;
              const snakeCaseField = fieldConfig.field.replace(
                /([A-Z])/g,
                (match) => '_' + match.toLowerCase()
              );

              let value = obj[camelCaseField] ?? obj[snakeCaseField];

              // If still not found, try the inverse conversion (snake_case to camelCase)
              if (value === undefined && camelCaseField.includes('_')) {
                const convertedField = camelCaseField.replace(/_([a-z])/g, (_, letter) =>
                  letter.toUpperCase()
                );
                value = obj[convertedField];
              }

              if (value === null || value === undefined || value === '') {
                return null; // Skip empty values
              }

              // Handle label: false = no label, undefined = auto-generate, string = use as-is
              const label =
                fieldConfig.label === false
                  ? false
                  : fieldConfig.label !== undefined
                    ? fieldConfig.label
                    : autoGenerateLabel(fieldConfig.field);

              // Format value based on type if specified
              let formattedValue = String(value);
              if (fieldConfig.type === 'currency' && typeof value === 'number') {
                formattedValue = `$${value.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;
              } else if (fieldConfig.type === 'date') {
                const date = new Date(value as string | number);
                if (!isNaN(date.getTime())) {
                  formattedValue = date.toLocaleDateString();
                }
              }

              // Only include label if it's not false
              return label !== false ? `${label}: ${formattedValue}` : formattedValue;
            })
            .filter((entry) => entry !== null);

          // If no configured fields were found, fall back to showing all fields
          // This ensures we don't hide data when field names don't match
          if (entries.length === 0) {
            // Fall through to default behavior below
          } else {
            const objectString = entries.join('\n');
            const separator = config.separator || '\n----\n';
            return index > 0 ? separator + objectString : objectString;
          }
        }

        // Default behavior: show all fields
        const entries = Object.entries(obj)
          .filter(([_, value]) => value !== null && value !== undefined && value !== '')
          .map(([key, value]) => {
            const label = generateColumnLabel(key);

            // Format currency values if they look like money fields
            let formattedValue = String(value);
            if (
              typeof value === 'number' &&
              (key.toLowerCase().includes('tuition') ||
                key.toLowerCase().includes('total') ||
                key.toLowerCase().includes('amount') ||
                key.toLowerCase().includes('fee'))
            ) {
              formattedValue = `$${value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`;
            }

            return `${label}: ${formattedValue}`;
          });

        // Join key-value pairs with line breaks, and separate objects with divider
        const objectString = entries.join('\n');
        return index > 0 ? '----\n' + objectString : objectString;
      })
      .filter((str) => str !== null)
      .join('\n');
  }

  // Handle serialized arrays or string representations
  const arrayStr = String(array);

  // If it's clearly an array of objects that got stringified improperly
  if (arrayStr.includes('[object Object]') || arrayStr.includes('object Object')) {
    return 'Complex data (unable to parse object details)';
  }

  // Try to parse as JSON
  if (arrayStr.startsWith('[') && arrayStr.endsWith(']')) {
    try {
      const parsed = JSON.parse(arrayStr);
      if (Array.isArray(parsed)) {
        return formatArrayObjects(parsed, config); // Recursive call with parsed array
      }
    } catch {
      // If parsing fails, provide a fallback message
      return 'Complex data (parse error)';
    }
  }

  return String(array);
};

// ============================================================================
// COMPONENT CLASS
// ============================================================================

export default class ProgramsTableComponent extends Component<ProgramsTableSignature> {
  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  columnMapping = COLUMN_MAPPING;

  private asResultRecord(item: unknown): Record<string, unknown> {
    if (item && typeof item === 'object') {
      return item as Record<string, unknown>;
    }

    return {};
  }

  // ============================================================================
  // STATE FOR DRAG & DROP
  // ============================================================================

  draggedColumnIndex?: number;

  @tracked reorderedColumns: DisplayColumn[] | null = null;

  @tracked dropIndicatorPosition?: number;

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  // Get the columns to display - either from selectedColumns prop or fallback to defaults
  get displayColumns(): DisplayColumn[] {
    let columns: DisplayColumn[] = [];

    if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
      columns = this.args.selectedColumns.map((columnKey): DisplayColumn => {
        // Normalize column key to camelCase for internal use
        // selectedColumns come in as snake_case from query params, but we use camelCase internally
        // to match the API response data format
        const normalizedKey = columnKey.includes('_') ? snakeToCamelCase(columnKey) : columnKey;

        // Use hardcoded mapping if available (check against original snake_case key)
        if (columnKey in this.columnMapping) {
          const config = this.columnMapping[columnKey as ColumnKey];
          return {
            key: normalizedKey, // Use camelCase key internally
            ...config,
          };
        } else {
          // Get a sample value from the first item for type inference
          const sampleItem = this.args.results.items?.[0];
          const sampleValue = sampleItem ? this.getCellValue(sampleItem, normalizedKey) : undefined;

          // Auto-generate label with context for table headers
          // Now we can pass camelCase only since we normalized above
          const label = generateSmartLabel(normalizedKey);

          return {
            key: normalizedKey, // Use camelCase key internally
            label,
            type: inferColumnType(normalizedKey, sampleValue),
            linkable: false as const,
          };
        }
      });
    } else {
      // No selection provided - show all columns from the first result item
      // Get all keys from the first item in results
      const firstItem = this.args.results.items?.[0];
      if (firstItem) {
        const itemKeys = Object.keys(this.asResultRecord(firstItem));

        columns = itemKeys.map((camelKey): DisplayColumn => {
          // Try to find mapping using the camelCase key
          // First check if there's a snake_case version in the mapping
          const snakeKey = camelKey.replace(/([A-Z])/g, (letter) => `_${letter.toLowerCase()}`);
          const mappingKey = (snakeKey in this.columnMapping ? snakeKey : camelKey) as ColumnKey;

          if (mappingKey in this.columnMapping) {
            const config = this.columnMapping[mappingKey];
            return {
              key: camelKey,
              ...config,
            };
          } else {
            // Auto-generate config for unmapped columns
            const sampleValue = this.getCellValue(firstItem, camelKey);
            return {
              key: camelKey,
              label: generateSmartLabel(camelKey),
              type: inferColumnType(camelKey, sampleValue),
              linkable: false as const,
            };
          }
        });
      }
    }

    if (this.args.columnOrder && this.args.columnOrder.length > 0) {
      columns = reorderTableColumns(
        columns as unknown as { key: string; [key: string]: unknown }[],
        this.args.columnOrder,
        snakeToCamelCase
      ) as unknown as DisplayColumn[];
    }

    // If columns were reordered via drag & drop, use that instead
    if (this.reorderedColumns) {
      return this.reorderedColumns;
    }

    return columns;
  }

  /**
   * Generate smart labels for table headers that keep necessary context
   * to disambiguate similar fields (e.g., "Program ID" vs "Application ID")
   *
  // ============================================================================
  // ROUTING METHODS
  // ============================================================================

  /**
   * Constructs full route from base route + sub route pattern.
   * Falls back to legacy routes if baseRoute is not provided.
   */
  buildRoute = (routePattern: string) => {
    if (this.args.baseRoute) {
      const fullRoute = `${this.args.baseRoute}.${routePattern}`;
      return fullRoute;
    }

    // Fallback to legacy routes if no baseRoute provided
    if (routePattern.includes('applications')) {
      return (
        this.args.applicationDetailRoute || 'authenticated.tuition-assistance.applications.show'
      );
    }
    if (routePattern.includes('program-instances')) {
      return (
        this.args.programDetailRoute || 'authenticated.tuition-assistance.program-instances.show'
      );
    }

    return routePattern;
  };

  // ============================================================================
  // DATA RETRIEVAL METHODS
  // ============================================================================

  /**
   * Gets the appropriate model ID for linking based on column and item data.
   * Handles different column types (application_id, program instance, etc.)
   * Falls back to program instance ID if application ID is missing.
   */
  getModelId = (item: unknown, columnKey: string) => {
    const typedItem = this.asResultRecord(item);

    // For application columns, look for application ID, fallback to program instance ID
    if (columnKey.includes('application')) {
      return (
        typedItem.tasApplicationIdKeyword ||
        typedItem.applicationId ||
        typedItem.tasProgramInstanceIdKeyword ||
        typedItem.programInstanceId ||
        typedItem.id ||
        ''
      );
    }

    // For program columns, look for program instance ID
    if (columnKey.includes('program')) {
      return (
        typedItem.tasProgramInstanceIdKeyword || typedItem.programInstanceId || typedItem.id || ''
      );
    }

    // Default fallback
    return typedItem.id || '';
  };

  // ============================================================================
  // FORMATTING METHODS
  // ============================================================================

  /**
   * Formats cell value based on column type.
   * Handles currency, dates, arrays, and text with dynamic array detection.
   */
  formatCellValue = (value: unknown, type: ColumnType, arrayConfig?: ArrayObjectsConfig) => {
    // Check for empty values first
    const isEmpty = value === null || value === undefined || value === '';

    // For currency type, show "N/A" when no value
    if (isEmpty && type === 'currency') {
      return 'N/A';
    }

    if (isEmpty) return '';

    // 🚀 DYNAMIC ARRAY DETECTION - Override type if we detect an array
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '';
      }

      // Check first element to determine array type
      const firstElement = value[0];
      if (
        typeof firstElement === 'object' &&
        firstElement !== null &&
        !(firstElement instanceof Date)
      ) {
        return formatArrayObjects(value, arrayConfig);
      } else {
        return formatArrayPrimitive(value);
      }
    }

    switch (type) {
      case 'currency': {
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        return isNaN(numValue)
          ? String(value)
          : `$${numValue.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
      }
      case 'date': {
        // Simple date formatting for now - can be enhanced to use safeFormatDate helper in template
        if (typeof value === 'string' || typeof value === 'number') {
          const date = new Date(value);
          return isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
        }
        return String(value);
      }
      case 'boolean': {
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        }
        // Handle string representations of booleans
        const strValue = String(value).toLowerCase();
        if (strValue === 'true' || strValue === '1') return 'Yes';
        if (strValue === 'false' || strValue === '0') return 'No';
        return String(value);
      }
      case 'array-primitive':
        return formatArrayPrimitive(value);

      case 'array-objects':
        return formatArrayObjects(value, arrayConfig);

      case 'text':
      default:
        return String(value);
    }
  };

  /**
   * Gets value from item using column key.
   */
  getCellValue = (item: unknown, columnKey: string) => {
    const typedItem = this.asResultRecord(item);
    let value = typedItem[columnKey];

    // Auto-convert payment amounts from cents to dollars
    // If the field name contains "payment" and ends with "amount", divide by 100
    if (
      typeof value === 'number' &&
      columnKey.toLowerCase().includes('payment') &&
      columnKey.toLowerCase().includes('amount')
    ) {
      value = value / 100;
    }

    return value !== undefined && value !== null ? value : '';
  };

  // ============================================================================
  // VALIDATION & CHECK METHODS
  // ============================================================================

  /**
   * Type guard to check if a column is linkable (has a route pattern).
   */
  isLinkableColumn = (linkable: string | false): linkable is string => {
    return typeof linkable === 'string' && linkable !== '';
  };

  /**
   * Checks if content contains newlines (indicating formatted array content).
   */
  isMultilineContent = (content: string) => {
    return typeof content === 'string' && content.includes('\n');
  };

  /**
   * Checks if a given state exists in the paymentStates filter.
   */
  containsPaymentState = (state: string) => {
    return this.args.paymentStates?.split(',').includes(state);
  };

  // ============================================================================
  // DRAG & DROP HANDLERS
  // ============================================================================

  /**
   * Handle drag start on column header
   */
  onDragStart = (index: number, event: DragEvent) => {
    this.draggedColumnIndex = index;
    this.dropIndicatorPosition = undefined;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  };

  /**
   * Handle drag over column header - show drop indicator
   */
  onDragOver = (index: number, event: DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    // Show drop indicator at this position
    this.dropIndicatorPosition = index;
  };

  /**
   * Handle drop on column header - reorder columns and log to console
   */
  onDrop = (dropIndex: number, event: DragEvent) => {
    event.preventDefault();
    this.dropIndicatorPosition = undefined;

    if (this.draggedColumnIndex === undefined || this.draggedColumnIndex === dropIndex) {
      this.draggedColumnIndex = undefined;
      return;
    }

    // Create new column order by moving dragged column to drop position
    const newColumns = [...this.displayColumns];
    const draggedColumnData = newColumns[this.draggedColumnIndex];

    if (!draggedColumnData) {
      this.draggedColumnIndex = undefined;
      return;
    }

    newColumns.splice(this.draggedColumnIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumnData);

    // Store the reordered columns for display
    this.reorderedColumns = newColumns;

    // Convert back to snake_case column keys for storage/API
    const newOrder = newColumns.map((col) => {
      const snakeKey = col.key.replace(/([A-Z])/g, (letter) => `_${letter.toLowerCase()}`);
      return snakeKey;
    });

    // Log the new order for testing
    console.log('Column order changed:', newOrder);
    console.log(
      'Column objects:',
      newColumns.map((c) => ({ key: c.key, label: c.label }))
    );

    // Call the callback if provided
    if (this.args.onColumnOrderChange) {
      this.args.onColumnOrderChange(newOrder);
    }

    this.draggedColumnIndex = undefined;
  };

  <template>
    <div ...attributes>
      <Table @isLoading={{@isLoading}}>
        <:thead as |options|>
          <options.tr>
            {{#each this.displayColumns as |column index|}}
              <options.th
                draggable="true"
                {{on "dragstart" (fn this.onDragStart index)}}
                {{on "dragover" (fn this.onDragOver index)}}
                {{on "drop" (fn this.onDrop index)}}
                class="cursor-move select-none relative
                  {{if (eq this.draggedColumnIndex index) 'opacity-50'}}"
              >
                {{column.label}}
                {{#if (eq this.dropIndicatorPosition index)}}
                  <div
                    class="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 pointer-events-none"
                  ></div>
                {{/if}}
              </options.th>
            {{/each}}
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{! @glint-expect-error: search results can have different types }}
          {{#each @results.items as |item|}}
            <options.tr class="align-top">
              {{#each this.displayColumns as |column|}}
                <options.td>
                  {{#if (this.isLinkableColumn column.linkable)}}
                    {{#let (this.getModelId item column.key) as |modelId|}}
                      {{#if modelId}}
                        <LinkTo
                          @route={{this.buildRoute column.linkable}}
                          @models={{array modelId}}
                          class="text-blue-600 hover:text-blue-800 underline"
                        >
                          {{#let
                            (this.formatCellValue
                              (this.getCellValue item column.key) column.type column.arrayConfig
                            )
                            as |formattedValue|
                          }}
                            {{#if (this.isMultilineContent formattedValue)}}
                              <div class="whitespace-pre-line">{{formattedValue}}</div>
                            {{else}}
                              {{formattedValue}}
                            {{/if}}
                          {{/let}}
                        </LinkTo>
                      {{else}}
                        {{#let
                          (this.formatCellValue
                            (this.getCellValue item column.key) column.type column.arrayConfig
                          )
                          as |formattedValue|
                        }}
                          {{#if (this.isMultilineContent formattedValue)}}
                            <div class="whitespace-pre-line">{{formattedValue}}</div>
                          {{else}}
                            {{formattedValue}}
                          {{/if}}
                        {{/let}}
                      {{/if}}
                    {{/let}}
                  {{else}}
                    {{#let
                      (this.formatCellValue
                        (this.getCellValue item column.key) column.type column.arrayConfig
                      )
                      as |formattedValue|
                    }}
                      {{#if (this.isMultilineContent formattedValue)}}
                        <div class="whitespace-pre-line">{{formattedValue}}</div>
                      {{else}}
                        {{formattedValue}}
                      {{/if}}
                    {{/let}}
                  {{/if}}
                </options.td>
              {{/each}}
            </options.tr>
          {{else}}
            <options.tr>
              <options.td colspan="{{this.displayColumns.length}}">
                <div class="text-sm text-gray-700">
                  <p>You have searched for applications reports with the following criteria and
                    found no results:</p>
                  {{#if @selectedFilters}}
                    <ul class="list-disc pl-5 flex flex-col gap-y-1">
                      {{#each-in @selectedFilters as |key value|}}
                        {{#if value}}
                          <li>{{key}}: {{value}}</li>
                        {{/if}}
                      {{/each-in}}
                    </ul>
                  {{/if}}
                </div>
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
        <:tfoot as |options|>
          <options.tr>
            <td colspan="15">
              <Paginator
                @totalItems={{@results.total}}
                @pageSize={{@limit}}
                @currentPage={{@page}}
                @onChange={{@onPageChange}}
              />
            </td>
          </options.tr>
        </:tfoot>
      </Table>
    </div>
  </template>
}
